// =============================================================
//  Desk AI — Moteur de réponses statiques (sans API)
//  Reconnaissance de mots-clés simple + suggestions de questions.
//  Synchronisé entre la version mobile et la version web.
// =============================================================

export const MAYA_NAME = 'Desk AI';

// Message d'accueil affiché à l'ouverture du chat.
export const WELCOME_MESSAGE =
  "Salut 👋 Je suis Desk AI, ton assistant MyConcours. " +
  "Je peux t'aider à trouver des documents, et je connais le contenu des " +
  "arrêtés officiels : conditions, dates, âge limite, pièces du dossier. " +
  "Pose-moi ta question !";

// Suggestions rapides affichées sous le message d'accueil.
export const QUICK_REPLIES = [
  'Comment trouver un concours ?',
  'Comment télécharger un document ?',
  'Comment payer un document ?',
  "Comment m'inscrire ?",
  'Contacter le support',
];

// Base de connaissances : chaque entrée a des mots-clés et une réponse.
// L'ordre compte : la première entrée qui matche est utilisée.
const KNOWLEDGE_BASE = [
  {
    keywords: ['bonjour', 'salut', 'coucou', 'bonsoir', 'hello', 'cc', 'yo'],
    answer:
      "Bonjour 😊 Ravie de te parler ! Comment puis-je t'aider aujourd'hui sur MyConcours ?",
  },
  {
    keywords: ['merci', 'thanks', 'cool', 'super', 'génial', 'parfait'],
    answer:
      "Avec plaisir 🙌 Si tu as d'autres questions, je reste disponible. Bonne réussite à tes concours !",
  },
  {
    keywords: ['ça va', 'comment vas', 'comment tu vas', 'tu vas bien'],
    answer:
      "Je vais très bien, merci 🤖 Et toi ? Dis-moi comment je peux t'aider sur tes concours.",
  },
  {
    keywords: ['concours', 'épreuve', 'epreuve', 'sujet', 'sujets', 'annale', 'annales'],
    answer:
      "Pour trouver un concours 📚 :\n\n" +
      "1. Va dans l'onglet Accueil\n" +
      "2. Utilise la barre de recherche en haut\n" +
      "3. Filtre par type \"Sujets d'Épreuves\", par année ou par langue\n\n" +
      "Tu peux aussi parcourir l'onglet Centres pour voir les concours par établissement.",
  },
  {
    keywords: ['télécharger', 'telecharger', 'download', 'pdf', 'ouvrir le document'],
    answer:
      "Pour consulter ou télécharger un document 📥 :\n\n" +
      "1. Ouvre le document depuis l'accueil\n" +
      "2. Si c'est gratuit, il s'ouvre directement\n" +
      "3. Si c'est payant, effectue le paiement puis retrouve-le dans \"Mes documents\"\n\n" +
      "Tes documents achetés restent accessibles à tout moment dans l'onglet Mes documents.",
  },
  {
    keywords: ['payer', 'paiement', 'prix', 'acheter', 'achat', 'fcfa', 'argent', 'coût', 'cout', 'campay', 'momo', 'orange money', 'mtn'],
    answer:
      "Pour payer un document 💳 :\n\n" +
      "1. Ouvre le document payant qui t'intéresse\n" +
      "2. Appuie sur le bouton d'achat\n" +
      "3. Choisis ton mode de paiement Mobile Money (MTN / Orange)\n" +
      "4. Valide la transaction sur ton téléphone\n\n" +
      "Une fois confirmé, le document est débloqué automatiquement dans \"Mes documents\".",
  },
  {
    keywords: ['inscrire', 'inscription', "s'inscrire", 'créer un compte', 'creer un compte', 'compte', 'register', 'enregistrer'],
    answer:
      "Pour créer ton compte 📝 :\n\n" +
      "1. Sur l'écran de connexion, appuie sur \"S'inscrire\"\n" +
      "2. Renseigne ton nom, email et mot de passe\n" +
      "3. Valide — et c'est parti !\n\n" +
      "Tu pourras ensuite compléter ton profil dans l'onglet Profil.",
  },
  {
    keywords: ['connexion', 'connecter', 'login', 'se connecter', 'mot de passe', 'mdp', 'password', 'oublié', 'oublie'],
    answer:
      "Pour te connecter 🔐 :\n\n" +
      "• Entre ton email et ton mot de passe sur l'écran de connexion.\n" +
      "• Mot de passe oublié ? Appuie sur \"Mot de passe oublié\" pour le réinitialiser par email.",
  },
  {
    keywords: ['centre', 'centres', 'établissement', 'etablissement', 'école', 'ecole', 'université', 'universite'],
    answer:
      "L'onglet Centres 🏫 regroupe les établissements et leurs concours.\n\n" +
      "Tu peux y parcourir les centres, voir leurs documents et concours disponibles, " +
      "et accéder aux détails de chacun.",
  },
  {
    keywords: ['profil', 'profile', 'modifier', 'photo', 'mes infos', 'changer'],
    answer:
      "Dans l'onglet Profil 👤 tu peux :\n\n" +
      "• Modifier tes informations personnelles\n" +
      "• Changer ta photo de profil\n" +
      "• Gérer le thème (clair / sombre)\n" +
      "• Accéder au support et aux infos de l'app",
  },
  {
    keywords: ['mes documents', 'historique', 'achetés', 'achetes', 'mes achats', 'documents achetés'],
    answer:
      "Tous tes documents achetés ou consultés se trouvent dans l'onglet \"Mes documents\" 📂. " +
      "Tu peux les rouvrir à tout moment, même hors connexion une fois téléchargés.",
  },
  {
    keywords: ['support', 'aide', 'contact', 'contacter', 'whatsapp', 'problème', 'probleme', 'bug', 'souci'],
    answer:
      "Besoin d'aide humaine ? 🆘\n\n" +
      "• Appuie sur le bouton WhatsApp vert sur l'accueil pour discuter directement avec notre équipe.\n" +
      "• Ou va dans Profil → Support.\n\n" +
      "On te répond rapidement 💬",
  },
  {
    keywords: ['thème', 'theme', 'sombre', 'clair', 'mode nuit', 'dark', 'light'],
    answer:
      "Pour changer le thème 🌗 : va dans l'onglet Profil, puis active/désactive le mode sombre. " +
      "Ton choix est mémorisé automatiquement.",
  },
  {
    keywords: ['langue', 'français', 'francais', 'anglais', 'english'],
    answer:
      "Les documents sont disponibles en Français 🇫🇷 et en Anglais 🇬🇧. " +
      "Utilise le filtre \"Langue\" dans la recherche pour n'afficher que ceux qui t'intéressent.",
  },
  {
    keywords: ['notification', 'notifications', 'cloche', 'alertes'],
    answer:
      "Appuie sur l'icône cloche 🔔 en haut de l'accueil pour voir tes notifications " +
      "(nouveaux documents, mises à jour, annonces).",
  },
  {
    keywords: ['qui es-tu', 'qui es tu', 'tu es qui', "c'est quoi maya", 'maya', 'ton nom', 'tu fais quoi'],
    answer:
      "Je suis Desk AI 🤖, ton assistant virtuel MyConcours ! " +
      "Je t'aide à naviguer dans l'app, trouver des documents et répondre à tes questions sur les concours.",
  },
];

// Réponse par défaut quand aucun mot-clé n'est reconnu.
const FALLBACK_ANSWERS = [
  "Hmm, je ne suis pas sûre d'avoir bien compris 🤔. Tu peux reformuler ? " +
    "Tu peux me parler de concours, documents, paiement, inscription ou support.",
  "Je n'ai pas encore la réponse à ça 😅. Essaie une des suggestions, " +
    "ou contacte notre équipe via le bouton WhatsApp pour une aide personnalisée.",
];

/**
 * Normalise un texte : minuscules + suppression des accents.
 */
const normalize = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Retourne la réponse de Desk AI pour un message donné.
 * 100% local — aucun appel réseau.
 */
export const getMayaResponse = (userMessage) => {
  const text = normalize(userMessage);

  for (const entry of KNOWLEDGE_BASE) {
    const matched = entry.keywords.some((kw) => text.includes(normalize(kw)));
    if (matched) return entry.answer;
  }

  // Aucune correspondance → réponse de repli (alternée pour varier).
  const idx = Math.floor(Math.random() * FALLBACK_ANSWERS.length);
  return FALLBACK_ANSWERS[idx];
};
